'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';

/**
 * Refresh interval for relative timestamps ("2 hours ago") after hydration.
 * Only runs on the client, so it never affects SSR / first-render output.
 */
const REFRESH_INTERVAL_MS = 60_000;

const ServerNowContext = createContext<number | null>(null);

/**
 * Provides a "now" timestamp that is identical during SSR and the first
 * client render (the server value, passed down as a prop), then upgrades to
 * the live clock after mount.
 *
 * This is what removes React error #425 ("Text content does not match
 * server-rendered HTML") on ad pages: date formatting used `dayjs()` at render
 * time, so SSR and hydration produced different relative strings. Anchoring
 * both to the same server timestamp makes the first paint deterministic while
 * keeping relative times live afterwards.
 */
export const ServerNowProvider = ({
  serverNow,
  children
}: PropsWithChildren<{ serverNow: number }>) => {
  const [now, setNow] = useState(serverNow);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <ServerNowContext.Provider value={now}>
      {children}
    </ServerNowContext.Provider>
  );
};

/**
 * Returns the stable "now" timestamp (ms epoch). Falls back to `Date.now()`
 * when used outside the provider (e.g. isolated unit tests), where
 * determinism across SSR/hydration does not apply.
 */
export const useStableNow = (): number => {
  const value = useContext(ServerNowContext);
  return value ?? Date.now();
};
